import React, { useCallback, useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import moment from "moment";
import { FilterControl, useFilterBar } from "./filter-bar";
import styles from "./filter-bar.module.scss";
import lang from "translations";

const DATE_PRESETS = [
  { value: "7d", label: lang.last7Days, days: 7 },
  { value: "30d", label: lang.last30Days, days: 30 },
  { value: "90d", label: lang.last90Days, days: 90 },
  { value: "custom", label: lang.customRange },
];

const DOW = ["S", "M", "T", "W", "T", "F", "S"];

const mergeDateAndTime = (date, time, endOfDay = false) => {
  if (!time) return endOfDay ? date.clone().endOf("day") : date.clone().startOf("day");
  return date
    .clone()
    .hour(time.hour())
    .minute(time.minute())
    .second(time.second())
    .millisecond(time.millisecond());
};

const getDateSummary = (value, fallback, singleDate = false) => {
  const [startDate, endDate] = value || [];
  if (!startDate) return fallback;
  if (singleDate || !endDate) return startDate.format("MMM D");
  if (startDate.isSame(endDate, "day")) return startDate.format("MMM D");
  return `${startDate.format("MMM D")} - ${endDate.format("MMM D")}`;
};

const FilterDateRange = ({
  id,
  label,
  maxDays = 90,
  onChange,
  value = [],
  singleDate = false,
  showPresets = true,
}) => {
  const { closeFilter } = useFilterBar();
  const [pendingRange, setPendingRange] = useState(value);
  const [calendarMonth, setCalendarMonth] = useState(
    (value[1] || value[0] || moment()).clone().startOf("month"),
  );
  const [activePreset, setActivePreset] = useState("custom");

  useEffect(() => {
    setPendingRange(value);
    setCalendarMonth((value[1] || value[0] || moment()).clone().startOf("month"));
  }, [value]);

  const resetPendingRange = useCallback(() => {
    setPendingRange(value);
    setCalendarMonth((value[1] || value[0] || moment()).clone().startOf("month"));
  }, [value]);

  const calendarDays = useMemo(() => {
    const firstWeekday = calendarMonth.clone().startOf("month").day();
    const daysInMonth = calendarMonth.daysInMonth();
    return [
      ...Array.from({ length: firstWeekday }, () => null),
      ...Array.from({ length: daysInMonth }, (_, index) => calendarMonth.clone().date(index + 1)),
    ];
  }, [calendarMonth]);

  const rangeText = useMemo(() => {
    const [startDate, endDate] = pendingRange || [];
    if (!startDate) return lang.pickADate;
    if (singleDate || !endDate) return startDate.format("MMM D");
    const start = moment.min(startDate, endDate);
    const end = moment.max(startDate, endDate);
    return `${start.format("MMM D")} - ${end.format("MMM D")}`;
  }, [pendingRange, singleDate]);

  const selectDate = useCallback((date) => {
    setActivePreset("custom");
    if (singleDate) {
      setPendingRange([date.clone().startOf("day"), date.clone().endOf("day")]);
      return;
    }
    setPendingRange(([startDate, endDate]) => {
      if (!startDate || endDate) return [date.clone().startOf("day"), null];
      return [startDate, date.clone().endOf("day")];
    });
  }, [singleDate]);

  const applyRange = useCallback(
    (range = pendingRange) => {
      const [rawStart, rawEnd] = range || [];
      if (!rawStart) return;
      const startDate = rawEnd ? moment.min(rawStart, rawEnd) : rawStart;
      const endDate = singleDate ? startDate : (rawEnd ? moment.max(rawStart, rawEnd) : rawStart);
      if (!singleDate && Math.abs(endDate.diff(startDate, "days")) > maxDays) return;

      onChange([
        mergeDateAndTime(startDate, value[0]),
        mergeDateAndTime(endDate, value[1], true),
      ]);
      closeFilter();
    },
    [closeFilter, maxDays, onChange, pendingRange, value, singleDate],
  );

  const applyPreset = useCallback(
    (preset) => {
      setActivePreset(preset.value);
      if (preset.value === "custom") return;
      const endDate = moment().endOf("day");
      const startDate = moment().subtract(preset.days - 1, "days").startOf("day");
      const range = [startDate, endDate];
      setPendingRange(range);
      setCalendarMonth(endDate.clone().startOf("month"));
      applyRange(range);
    },
    [applyRange],
  );

  const isDateDisabled = useCallback(
    (day) => {
      const [startDate, endDate] = pendingRange || [];
      return Boolean(startDate && !endDate && Math.abs(day.diff(startDate, "days")) > maxDays);
    },
    [maxDays, pendingRange],
  );

  return (
    <FilterControl
      id={id}
      label={label}
      summary={getDateSummary(value, label, singleDate)}
      menuClassName={styles.dateMenu}
      onOpen={resetPendingRange}
    >
      <div className={styles.calendarWrap}>
        {showPresets && (
          <div className={styles.presets}>
            {DATE_PRESETS.map((preset) => (
              <button
                key={preset.value}
                type="button"
                className={classnames(styles.preset, {
                  [styles.presetActive]: activePreset === preset.value,
                })}
                disabled={preset.days > maxDays}
                onClick={() => applyPreset(preset)}
              >
                {preset.label}
              </button>
            ))}
          </div>
        )}
        <div className={styles.calendar}>
          <div className={styles.calendarHead}>
            <button
              type="button"
              className={styles.navButton}
              onClick={() => setCalendarMonth((current) => current.clone().subtract(1, "month"))}
            >
              &lt;
            </button>
            <span>{calendarMonth.format("MMMM YYYY")}</span>
            <button
              type="button"
              className={styles.navButton}
              onClick={() => setCalendarMonth((current) => current.clone().add(1, "month"))}
            >
              &gt;
            </button>
          </div>
          <div className={styles.calendarGrid}>
            {DOW.map((day, index) => (
              <div key={`${day}-${index}`} className={styles.calendarDow}>
                {day}
              </div>
            ))}
            {calendarDays.map((day, index) => {
              const [rawStart, rawEnd] = pendingRange || [];
              const startDate = rawStart && rawEnd ? moment.min(rawStart, rawEnd) : rawStart;
              const endDate = rawStart && rawEnd ? moment.max(rawStart, rawEnd) : rawStart;
              const inRange = day && startDate && endDate && day.isBetween(startDate, endDate, "day", "[]");
              const rangeEnd = day && startDate && endDate &&
                (day.isSame(startDate, "day") || day.isSame(endDate, "day"));
              const disabled = day && isDateDisabled(day);

              return (
                <div key={day ? day.format("YYYY-MM-DD") : `blank-${index}`} className={styles.calendarCell}>
                  {day && (
                    <button
                      type="button"
                      className={classnames(styles.calendarDay, {
                        [styles.calendarDayRange]: inRange && !rangeEnd,
                        [styles.calendarDayEnd]: rangeEnd,
                      })}
                      disabled={disabled}
                      onClick={() => selectDate(day)}
                    >
                      {day.date()}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <div className={styles.calendarFoot}>
            <span>{rangeText}</span>
            <button type="button" className={styles.applyButton} onClick={() => applyRange()}>
              {lang.apply}
            </button>
          </div>
        </div>
      </div>
    </FilterControl>
  );
};

export default FilterDateRange;
